import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import axios from 'axios'
import { supabase } from '../lib/supabase'

function detectDevice() {
  const ua = navigator.userAgent
  let browser = 'Unknown'
  if (/Edg\//.test(ua)) browser = 'Edge'
  else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser = 'Chrome'
  else if (/Firefox\//.test(ua)) browser = 'Firefox'
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = 'Safari'
  let os = 'Unknown'
  if (/Windows NT/.test(ua)) os = 'Windows'
  else if (/Mac OS X/.test(ua) && !/iPhone|iPad|iPod/.test(ua)) os = 'macOS'
  else if (/Android/.test(ua)) os = 'Android'
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS'
  else if (/Linux/.test(ua)) os = 'Linux'
  return `${browser} on ${os}`
}

export default function BugReportModal({ onClose }) {
  const [description, setDescription] = useState('')
  const [pageUrl, setPageUrl] = useState('')
  const [device] = useState(detectDevice())
  const [screenshot, setScreenshot] = useState(null)
  const [status, setStatus] = useState('idle') // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => { setPageUrl(window.location.href) }, [])

  async function handleSubmit() {
    if (!description.trim()) { setErrorMsg('Please describe what happened.'); return }
    if (screenshot && screenshot.size > 5 * 1024 * 1024) {
      setErrorMsg('Screenshot must be under 5 MB.'); return
    }
    setStatus('loading')
    setErrorMsg('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) { setErrorMsg('You must be signed in to report a bug.'); setStatus('error'); return }
      const fd = new FormData()
      fd.append('description', description.slice(0, 1000))
      fd.append('pageUrl', pageUrl)
      fd.append('device', device)
      if (screenshot) fd.append('screenshot', screenshot)
      await axios.post('/api/bug-reports', fd, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      setStatus('success')
      setTimeout(onClose, 1500)
    } catch (err) {
      setErrorMsg(err.response?.data?.error ?? 'Failed to submit. Please try again.')
      setStatus('error')
    }
  }

  return createPortal(
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()} onMouseDown={(e) => e.stopPropagation()}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Report a bug</h2>
        {status === 'success' ? (
          <p className="modal-desc" style={{ color: '#10b981' }}>✓ Thanks! Your report was sent.</p>
        ) : (
          <>
            <p className="modal-desc">Tell us what went wrong. We'll look into it.</p>

            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4, fontWeight: 600 }}>
              What happened? <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <textarea
              className="auth-input"
              rows={5}
              maxLength={1000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the bug…"
              style={{ marginBottom: 4, resize: 'vertical', width: '100%' }}
            />
            <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)', textAlign: 'right', marginBottom: 12 }}>
              {description.length}/1000
            </div>

            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4, fontWeight: 600 }}>Page</label>
            <input
              className="auth-input"
              value={pageUrl}
              onChange={(e) => setPageUrl(e.target.value)}
              style={{ marginBottom: 12, width: '100%' }}
            />

            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4, fontWeight: 600 }}>Device</label>
            <input
              className="auth-input"
              value={device}
              disabled
              style={{ marginBottom: 12, opacity: 0.7, width: '100%' }}
            />

            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4, fontWeight: 600 }}>
              Screenshot <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}>(optional, max 5 MB)</span>
            </label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={(e) => setScreenshot(e.target.files?.[0] ?? null)}
              style={{ marginBottom: 12, width: '100%', fontSize: '0.85rem' }}
            />

            {errorMsg && (
              <p style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: 8 }}>{errorMsg}</p>
            )}

            <div className="modal-actions">
              <button className="btn btn-outline" onClick={onClose}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={status === 'loading' || !description.trim()}
              >
                {status === 'loading' ? 'Sending…' : 'Send report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  )
}
